import { Flex } from '@chakra-ui/react';
import { AppointmentResponse } from '@web-app/hooks/queries/appointment-by-room-name/types';
import { useGetPreviousChartingNotes } from '@web-app/hooks/queries/charting-notes';
import type { PatientChartingNoteRow } from '@web-app/server/services/charting-notes/charting-notes.service';
import { FC, useMemo } from 'react';

import { ChartHistoryUI, ChartNote } from './ChartHistoryUI';

interface ChartHistoryProps {
  appointment?: AppointmentResponse;
}

/**
 * Maps PatientChartingNoteRow (from the shared tRPC endpoint) to the
 * ChartNote shape that ChartHistoryUI and ChartingNote components expect.
 */
function toChartNote(row: PatientChartingNoteRow): ChartNote {
  return {
    id: row.id,
    form_answer_group: {
      id: row.id,
      name: row.title,
      created_at: row.appointmentDate ?? '',
      metadata: row.metadata,
      form_answers: row.answers.map((a) => ({
        label: a.label,
        answer: a.answer,
      })),
      appointment: row.appointmentDate
        ? {
            id: '',
            date: row.appointmentDate,
            appointment_type: { name: row.title },
          }
        : undefined,
    },
  };
}

/**
 * V2 version of chart history component for CallV2 layout.
 * Displays previous charting notes for the patient.
 * Positioned in the side pane as a tab.
 */
export const ChartHistory: FC<ChartHistoryProps> = ({ appointment }) => {
  const {
    data: chartingNotes,
    isLoading,
    isError,
  } = useGetPreviousChartingNotes(appointment?.client_id);

  const chartNotes: ChartNote[] = useMemo(
    () => (chartingNotes ?? []).map(toChartNote),
    [chartingNotes],
  );

  return (
    <Flex
      h="100%"
      direction="column"
      overflowY="auto"
      position="relative"
      data-testid="chart-history-v2"
    >
      <ChartHistoryUI
        chartNotes={chartNotes}
        isLoading={isLoading}
        isError={isError}
      />
    </Flex>
  );
};
